import { useCallback, useEffect, useState } from 'react';
import { useIsAdmin } from '../api/auth';

/**
 * Choosing a lens for a camera.
 *
 * The sibling of the camera picker, and it splits its inputs the same way: the
 * top row is what the lens must PHYSICALLY do — sit on the camera's mount, cover
 * its sensor, resolve its megapixels — and no ordering brings back a lens that
 * cannot. Below that sits the framing: a working distance and a field of view
 * turn into the focal length the glass has to hit. Leave the framing blank and
 * the match is on fit and brightness alone, for when the camera is chosen but the
 * mounting distance is not yet fixed.
 *
 * The field of view and the sensor dimension are the SAME axis — both the width
 * across the belt, or both the height along it. The sensor's physical size in mm
 * is asked for directly rather than guessed from the format string, because a
 * format does not fix an aspect ratio and a wrong guess reframes the whole view.
 */

interface Lens {
  vendor: string;
  model: string;
  family: string;
  type: string;
  focalMinMm: number;
  focalMaxMm: number;
  aperture: number | null;
  mount: string;
  sensorFormat: string | null;
  resolutionMP: number | null;
  iris: string | null;
  spectrum: string;
  focalFit: 'exact' | 'in-range' | 'any';
  focalErrorMm: number;
  resolutionUnverified: boolean;
  formatUnverified: boolean;
  docsUrl: string | null;
  listPrice: number | null;
  currency: string;
  priceRetrievedAt: string | null;
}

interface Result {
  considered: number;
  matched: number;
  returned: number;
  requiredFocalMm: number | null;
  lenses: Lens[];
}

interface Vendor {
  name: string;
  models: number;
  source: string;
  caveat: string | null;
}

const MOUNTS = ['C', 'CS', 'F', 'M42', 'M58', 'TFL', 'TFL-II', 'S'] as const;

const cell: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', whiteSpace: 'nowrap' };
const num: React.CSSProperties = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export function LensPickerView() {
  const isAdmin = useIsAdmin();

  // The camera the lens sits on. Defaults describe a common 2/3" 5 MP C-mount
  // machine-vision camera so the page opens with results, not an empty table.
  const [mount, setMount] = useState('C');
  const [megapixels, setMegapixels] = useState('5');
  const [sensorFormat, setSensorFormat] = useState('2/3"');
  const [spectrum, setSpectrum] = useState('visible');

  // Framing — all three or none. Empty by default: without a working distance
  // there is no honest focal length, and a made-up one is worse than none.
  const [workingDistanceMm, setWorkingDistanceMm] = useState('');
  const [fieldOfViewMm, setFieldOfViewMm] = useState('');
  const [sensorMm, setSensorMm] = useState('');

  const [vendors, setVendors] = useState<string[]>([]);
  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/suppliers/lenses/vendors')
      .then((r) => (r.ok ? r.json() : []))
      .then(setVendorList)
      .catch(() => undefined);
  }, []);

  const search = useCallback(async () => {
    setBusy(true);
    setError(null);
    const params = new URLSearchParams({ mount, megapixels, limit: '25' });
    if (sensorFormat.trim()) params.set('sensorFormat', sensorFormat.trim());
    if (spectrum !== 'visible') params.set('spectrum', spectrum);
    // Sent as a trio or not at all — the API rejects a partial framing, and the
    // page should not ask it something it will refuse.
    if (workingDistanceMm && fieldOfViewMm && sensorMm) {
      params.set('workingDistanceMm', workingDistanceMm);
      params.set('fieldOfViewMm', fieldOfViewMm);
      params.set('sensorMm', sensorMm);
    }
    for (const v of vendors) params.append('vendors', v);
    try {
      const res = await fetch(`/api/suppliers/lenses?${params}`);
      if (!res.ok) throw new Error((await res.json()).message ?? 'Search failed');
      setResult(await res.json());
    } catch (e) {
      setError((e as Error).message);
      setResult(null);
    } finally {
      setBusy(false);
    }
  }, [mount, megapixels, sensorFormat, spectrum, workingDistanceMm, fieldOfViewMm, sensorMm, vendors]);

  useEffect(() => {
    void search();
  }, [search]);

  const adopt = async (lens: Lens) => {
    setError(null);
    try {
      const res = await fetch(
        `/api/suppliers/lenses/${encodeURIComponent(lens.vendor)}/${encodeURIComponent(lens.model)}/adopt`,
        { method: 'POST' },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.message ?? 'Could not adopt');
      setNotice(
        body.created
          ? `${lens.model} added to the catalogue — set your own price there.`
          : `${lens.model} was already in the catalogue.`,
      );
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const money = (v: number | null, currency: string) =>
    v === null ? '—' : `${currency === 'EUR' ? '€' : currency} ${v.toLocaleString('nl-NL')}`;

  const focalLabel = (l: Lens) =>
    l.focalMinMm === l.focalMaxMm ? `${l.focalMinMm} mm` : `${l.focalMinMm}–${l.focalMaxMm} mm`;

  // The framing is complete only as a trio; a partial one is a half-asked
  // question, so it is neither sent nor treated as an error until submitted.
  const framingPartial =
    [workingDistanceMm, fieldOfViewMm, sensorMm].filter((v) => v.trim()).length;
  const framingIncomplete = framingPartial > 0 && framingPartial < 3;

  return (
    <div>
      <h1>Lens selection</h1>
      <p className="muted" style={{ marginTop: -8, maxWidth: 700 }}>
        Describe the camera the lens sits on, and — if you know the mounting
        distance — the field of view you want. Everything listed fits the camera;
        add a working distance to rank by how closely each frames the part.
      </p>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>Mount</div>
            <select value={mount} onChange={(e) => setMount(e.target.value)} style={{ width: 90 }}>
              {MOUNTS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <Field
            label="Camera resolution"
            unit="MP"
            value={megapixels}
            onChange={setMegapixels}
            hint="The lens must resolve at least this"
          />
          <label style={{ display: 'block' }}>
            <div style={{ fontSize: 13, marginBottom: 4 }}>
              Sensor format <span className="muted">(in)</span>
            </div>
            <input
              value={sensorFormat}
              onChange={(e) => setSensorFormat(e.target.value)}
              placeholder={'2/3"'}
              style={{ width: 90 }}
            />
            <div className="muted" style={{ fontSize: 11, marginTop: 3, maxWidth: 130 }}>
              The lens must cover this
            </div>
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 13 }}>Spectrum</span>
            <select value={spectrum} onChange={(e) => setSpectrum(e.target.value)}>
              <option value="visible">Visible</option>
              <option value="SWIR">SWIR</option>
              <option value="UV">UV</option>
            </select>
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 18,
            flexWrap: 'wrap',
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px solid var(--border)',
            alignItems: 'flex-end',
          }}
        >
          <span
            className="muted"
            style={{ fontSize: 13, alignSelf: 'center', maxWidth: 150, lineHeight: 1.4 }}
          >
            Framing <span style={{ opacity: 0.7 }}>(optional — all three together)</span>
          </span>
          <Field
            label="Working distance"
            unit="mm"
            value={workingDistanceMm}
            onChange={setWorkingDistanceMm}
            hint="Lens to part"
          />
          <Field
            label="Field of view"
            unit="mm"
            value={fieldOfViewMm}
            onChange={setFieldOfViewMm}
            hint="Across one axis"
          />
          <Field
            label="Sensor size"
            unit="mm"
            value={sensorMm}
            onChange={setSensorMm}
            hint="Same axis as the FoV"
          />
        </div>
        {framingIncomplete && (
          <div className="muted" style={{ fontSize: 12, marginTop: 10, color: 'var(--warn, #f59e0b)' }}>
            Give all three framing values to compute a focal length, or clear them to match on fit
            alone.
          </div>
        )}
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>}
      {notice && (
        <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>
      )}

      <div className="panel">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div className="muted" style={{ fontSize: 13 }}>
            {busy
              ? 'Searching…'
              : result
                ? `${result.matched} of ${result.considered} lenses fit${
                    result.matched > result.returned ? `, showing ${result.returned}` : ''
                  }`
                : ''}
          </div>
          {result?.requiredFocalMm != null && (
            <div className="muted" style={{ fontSize: 13 }}>
              Needs about{' '}
              <strong style={{ color: 'var(--text)' }}>{result.requiredFocalMm} mm</strong> —
              ±15% is offered, so check a lens calculator before ordering.
            </div>
          )}
        </div>

        {result && result.lenses.length === 0 && (
          <div className="muted" style={{ padding: '20px 0' }}>
            Nothing fits. The usual causes are a mount no lens in the range uses, a sensor larger
            than any lens covers, or a field of view that needs a focal length outside the range.
            Loosen one and try again.
          </div>
        )}

        {result && result.lenses.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr className="muted" style={{ fontSize: 12 }}>
                  <th style={cell}>Lens</th>
                  <th style={cell}>Type</th>
                  <th style={num}>Focal length</th>
                  <th style={num}>Aperture</th>
                  <th style={cell}>Covers</th>
                  <th style={num}>Resolves</th>
                  <th style={num}>Price</th>
                  <th style={cell} />
                </tr>
              </thead>
              <tbody>
                {result.lenses.map((l) => (
                  <tr key={`${l.vendor}-${l.model}`}>
                    <td style={cell}>
                      <div style={{ fontWeight: 600 }}>{l.model}</div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {l.vendor} · {l.family} · {l.mount}-mount
                        {l.spectrum !== 'visible' ? ` · ${l.spectrum}` : ''}
                      </div>
                    </td>
                    <td style={cell}>
                      {l.type}
                      {l.focalFit === 'in-range' && (
                        <div
                          className="muted"
                          style={{ fontSize: 11 }}
                          title="A zoom or varifocal covers the needed focal length within its range — set it by hand at install."
                        >
                          set at install
                        </div>
                      )}
                    </td>
                    <td style={num}>
                      {focalLabel(l)}
                      {l.focalFit === 'exact' && l.focalErrorMm !== 0 && (
                        <div className="muted" style={{ fontSize: 11 }}>
                          {l.focalErrorMm > 0 ? '+' : ''}
                          {l.focalErrorMm} mm vs. need
                        </div>
                      )}
                    </td>
                    <td style={num}>{l.aperture != null ? `F${l.aperture}` : '—'}</td>
                    <td style={cell}>
                      {l.sensorFormat ?? '—'}
                      {l.formatUnverified && (
                        <span
                          title="The vendor does not publish this lens's image format, so sensor coverage could not be checked — confirm on the datasheet before ordering."
                          style={{ color: 'var(--warn, #f59e0b)', marginLeft: 6, fontSize: 11 }}
                        >
                          ?
                        </span>
                      )}
                    </td>
                    <td style={num}>
                      {l.resolutionMP != null ? `${l.resolutionMP} MP` : '—'}
                      {l.resolutionUnverified && (
                        <span
                          title="The vendor does not rate this lens's resolution, so it was not checked against the camera — confirm before ordering."
                          style={{ color: 'var(--warn, #f59e0b)', marginLeft: 6, fontSize: 11 }}
                        >
                          ?
                        </span>
                      )}
                    </td>
                    <td style={num}>{money(l.listPrice, l.currency)}</td>
                    <td style={cell}>
                      {isAdmin && (
                        <button className="btn sm" onClick={() => void adopt(l)}>
                          Add to catalogue
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {vendorList.length > 0 && (
          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {vendorList.map((v) => (
              <label
                key={v.name}
                style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}
                title={v.caveat ?? undefined}
              >
                <input
                  type="checkbox"
                  checked={vendors.includes(v.name)}
                  onChange={(e) =>
                    setVendors((cur) =>
                      e.target.checked ? [...cur, v.name] : cur.filter((x) => x !== v.name),
                    )
                  }
                />
                {v.name} ({v.models})
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  unit,
  value,
  onChange,
  hint,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>
        {label} <span className="muted">({unit})</span>
      </div>
      <input
        value={value}
        inputMode="decimal"
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 110 }}
      />
      {hint && (
        <div className="muted" style={{ fontSize: 11, marginTop: 3, maxWidth: 130 }}>
          {hint}
        </div>
      )}
    </label>
  );
}
